/* eslint-disable max-len */
/* eslint-disable no-param-reassign */
import React, { useCallback, useContext } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import { MetadataContent } from '@jutro/legacy/uiconfig';
import '@xengage/gw-platform-translations';
import MLPAAgentCovTermComponent from '../MLPAAgentCovTermComponent/MLPAAgentCovTermComponent';
import MLClauseScheduledItemsComponent from '../MLClauseScheduledItemsComponent/MLClauseScheduledItemsComponent';
import metadata from './MLPAEditableCoveragesComponent.metadata.json5';
import styles from './MLPAEditableCoveragesComponent.module.scss';
import { IntlContext } from '@jutro/legacy/locale';
import { MLProductCodeUtil } from 'ml-portals-util-js';


function MLPAEditableCoveragesComponent(props) {
    const {
        model,
        onCovChange,
        loading,
        onGeneralChange,
        isDisplayOnly,
        allowSelectCoverage,
        level,
        vehicle,
        displayedLineCoverages,
        filterData,
        isRewrite,
        namesMap,
        vehiclesLength,
        isModernProduct,
        submissionVM
    } = props;
    const isVehicleLevel = level === 'vehicle';
    const intl = useContext(IntlContext);
    const { refreshData } = useDataRefresh();
    const EMPTY_STRING = 'Empty';
    const productCode = _.get(submissionVM, 'baseData.productCode.value', '');
    const isPersonalAuto = MLProductCodeUtil.isPersonalAuto(productCode);
     const isModernProductAuto = isModernProduct && isPersonalAuto

    const isEmpty = (obj) => {
        return obj.name === EMPTY_STRING || obj.name.indexOf(`- ${EMPTY_STRING}`) > -1;
    }

    const formatAmountString = useCallback((amount) => {
        try {
            return intl.formatNumber(amount.amount, { style: 'currency', currency: amount.currency });
        } catch (e) {
            return '';
        }
    }, [intl]);

    const formatAmountStringForOptCov = useCallback((amountObj, amount) => {
        try {
            return intl.formatNumber(amount, { style: 'currency', currency: amountObj.currency });
        } catch (e) {
            return '';
        }
    }, [intl]);

    const hasVehicleCovCosts = useCallback((coverage) => {
        return !!vehicle && coverage.vehicleCovCosts_ML && coverage.vehicleCovCosts_ML.length &&
            coverage.vehicleCovCosts_ML.find((vehicleCovCost) => vehicleCovCost.vehiclePublicID === vehicle.publicID);
    }, [vehicle]);

    const covAmountIsZero = (cov) => {
        let amt = 0;
        if (cov.lineCovCost_Ext && cov.lineCovCost_Ext.amount) {
            amt = cov.lineCovCost_Ext.amount;
        }
        return amt === 0;
    };

    const isTrailer = (veh) => {
        return veh.vehicleCategoryCode_Ext === 'trailer';
    };

    const hasSuspendedLiability = (veh) => {
        // look at selected attribute for Suspended Liability coverage using vehicle public id to look up       
        const suspendLiabCov = veh?.coverages?.find((cov) => cov.code_ML === 'PASuspendLiabilityCov_ML');
        return !!suspendLiabCov && suspendLiabCov.selected;
    };

    const isStorageOrCamperTrailer = (veh) => {
        const isStorage = veh?.vehiclePrimaryUse_Ext === 'storage_np';
        const isCamperTrailer = veh?.vehicleCategoryCode_Ext === 'camper_np' || veh?.vehicleCategoryCode_Ext === 'trailer_np';
        return !!isStorage || !!isCamperTrailer;
    };

    const covAmountDisplayed = useCallback((coverage, v) => {
        if (displayedLineCoverages?.has(coverage.code_ML)) {
            const vehId = displayedLineCoverages.get(coverage.code_ML);
            return vehId !== v.publicID;
        }
        return false;
    }, [displayedLineCoverages]);

    const getVehicleCostForCoverage = useCallback((coverage) => {
        if(!(hasSuspendedLiability(vehicle) && !loading && (coverage.name === "Combined Single Limit Liability" || coverage.name === "Medical Expense" || coverage.name === "Uninsured Motorists - Bodily Injury" || coverage.name === "Bodily Injury Liability" || coverage.name === "Property Damage Liability" || coverage.name === "Underinsured Motorists - Bodily Injury" || coverage.name === "Personal Injury Protection"))){
            if (vehicle && coverage.vehicleCovCosts_ML) {
                const currentVehicle = coverage.vehicleCovCosts_ML.find((vehicleCovCost) => vehicleCovCost.vehiclePublicID === vehicle.publicID);
                if (currentVehicle) {
                    return formatAmountString(currentVehicle?.cost);
                }
            }
        }
    }, [formatAmountString, loading, vehicle]);

    const getPolicyLevelCostForCoverage = useCallback((coverage, v) => {
        if (_.isNil(v) || isTrailer(v) || hasSuspendedLiability(v) || covAmountIsZero(coverage)) {
            return;
        }
        if (!covAmountDisplayed(coverage, vehicle)) {
            displayedLineCoverages?.set(coverage.code_ML, vehicle.publicID);

            return formatAmountString(coverage.lineCovCost_Ext);
        }
        return 'Included';
    }, [covAmountDisplayed, displayedLineCoverages, formatAmountString, vehicle]);

    const getCoverageCostBreakup = useCallback((coverage) => {
        if(!_.isUndefined(coverage) && !vehicle && coverage.selected === true && (_.isUndefined(coverage.amount) || _.isUndefined(coverage.amount.amount) || coverage.amount.amount <= 0) && (coverage.name === "Local Delivery Coverage"|| coverage.name === "Ride Share Coverage" || coverage.name === "Worldwide Liability" || coverage.name === "Named Non-Owner Coverage" || coverage.name === "Non-Owner")) {
            return "Included";
        } 

        if(!_.isUndefined(coverage) && !vehicle && !!coverage.amount && (coverage.amount.amount > 0)) { 
            if((coverage.name === "Diminishing Deductible" || coverage.name === "Auto Extra" || coverage.name === "Travel Package") && vehiclesLength) {                
                return formatAmountStringForOptCov(coverage.amount, coverage.amount.amount / vehiclesLength)
            } else {
                return formatAmountString(coverage.amount);
            }
        }
    }, []);  
        
    const hasScheduleItems = (cov) => !_.isEmpty(cov.scheduledItems_ML);

    const hasEmptyScheduleItems = useCallback((cov, schedCode) => {
        const hasSchedAndEmpty = hasScheduleItems(cov) && _.isEmpty(cov.scheduledItems_ML[schedCode]);
        return hasSchedAndEmpty &&
            cov.terms.length > 0;
    }, []);

    const coverageSelectedChanged = useCallback((isSelected, coverage) => {
        _.set(coverage, 'selected', isSelected);
        refreshData();
    }, [refreshData]);

    const getFilteredCovs = () => {
        let covs = _.filter(model, filterData)

        if (isRewrite && namesMap) {
            covs = covs.filter((cov) => {
                return namesMap.some((elm) => cov.name.includes(elm.coverageName ))
            });
        }

        return covs;
    };

    const isTrailerOrCamper = (veh) => {
        const isCamperTrailer = veh?.vehicleCategoryCode_Ext === 'camper_np' || veh?.vehicleCategoryCode_Ext === 'trailer_np';
        return !!isCamperTrailer;
    }
    const isMexicoOrDeclining = (cov) => {
      return (
        cov.name === "Limited Mexico Coverage" ||
        cov.name === "Declining Deductible"
      );
    };

    const hasCompOrCollision = (veh) => {
      const vehicleCovs = _.get(veh, "coverages", []);

      return vehicleCovs.some((coverage) => {
        return (
          coverage.selected &&
          (coverage.name === "Comprehensive" ||
            coverage.name === "Collision" ||
            coverage.code_ML === "PAComprehensiveCov" ||
            coverage.code_ML === "PACollisionCov")
        );
      });
    };
    const generateCovOverrides = useCallback(() => {
        const covOverrides = getFilteredCovs().map((cov, index) => {  
              const vehicleHasCompOrCollision = hasCompOrCollision(vehicle);     
              const disableMexicoAndDeclining = isModernProductAuto && 
              isMexicoOrDeclining(cov) && !vehicleHasCompOrCollision;     
            return {
                [`scrollingTableBodyDiv${index}`]: {
                    className: index === 0 ? 'ml-scrolling-table__body-option group-first row divider' : 'ml-scrolling-table__body-option group-first row'
                },
                [`editableCoverageSection${index}`]: {
                    visible: !isDisplayOnly
                },
                [`vehicleLevelCheckboxContainer${index}`]: {
                    visible: isVehicleLevel && !(cov.name === "Diminishing Deductible" || cov.name === "Auto Extra" || cov.name === "Travel Package" || cov.name === "Local Delivery Coverage"|| cov.name === "Ride Share Coverage" || cov.name === "Worldwide Liability" || cov.name === "Named Non-Owner Coverage" || cov.name === "Non-Owner")
                },
                [`editableCheckboxVehicle${index}`]: {
                    label: cov.name,
                    hideLabel: true,
                    visible: !(cov.name === "Diminishing Deductible" || cov.name === "Auto Extra" || cov.name === "Travel Package" || cov.name === "Local Delivery Coverage"|| cov.name === "Ride Share Coverage" || cov.name === "Worldwide Liability" || cov.name === "Named Non-Owner Coverage" || cov.name === "Non-Owner"),
                    disabled: cov.required || loading || isDisplayOnly || isEmpty(cov) || disableMexicoAndDeclining,
                    onValueChange: coverageSelectedChanged,
                    path: cov,
                    value: _.get(cov, 'selected')
                },
                [`vehicleLevelCovCost${index}`]: {
                    visible:!disableMexicoAndDeclining &&!!vehicle && !cov.isLineCovCost_Ext && hasVehicleCovCosts(cov) && !(cov.name === 'Declining Deductible' && isTrailerOrCamper(vehicle)),
                    content: getVehicleCostForCoverage(cov)
},
                [`policyLevelCovCost${index}`]: {
                    visible: !!vehicle && cov.isLineCovCost_Ext,
                    content: getPolicyLevelCostForCoverage(cov, vehicle)
                },
                [`covAmount${index}`]: {
                    label: cov.name,
                    visible: !disableMexicoAndDeclining && (((!vehicle && !!cov.amount && cov.amount.amount > 0) || (cov?.selected === true && (_.isUndefined(cov.amount) || _.isUndefined(cov.amount.amount) || cov.amount.amount <= 0) && (cov.name === "Local Delivery Coverage"|| cov.name === "Ride Share Coverage" || cov.name === "Worldwide Liability" || cov.name === "Named Non-Owner Coverage" || cov.name === "Non-Owner"))) && !(cov.name === "Declining Deductible" && isTrailerOrCamper(vehicle))),
                    content: getCoverageCostBreakup(cov)
                },
                [`displayMessageOnOptCovRemove${index}`]: {
                    visible: !_.get(cov, 'selected') && !loading && (cov.name === "Diminishing Deductible" || cov.name === "Auto Extra" || cov.name === "Travel Package"),
                    content: 'Not Applicable'               
                },
                [`displayMessageNotIncludedSusLiability${index}`]: {
                    visible: (hasSuspendedLiability(vehicle) || isStorageOrCamperTrailer(vehicle)) && !loading && (cov.name === "Combined Single Limit Liability" || cov.name === "Medical Expense" || cov.name === "Medical" || cov.name === "Uninsured Motorists - Bodily Injury" || cov.name === "Uninsured Motorist - Bodily Injury" || cov.name === "Bodily Injury Liability" || cov.name === "Property Damage Liability" || cov.name === "Underinsured Motorists - Bodily Injury" || cov.name === "Underinsured Motorist - Bodily Injury" || cov.name === "Personal Injury Protection" || cov.name === "Personal Injury Protection " || cov.name === "Uninsured Motorists - Property Damage" || cov.name === "Uninsured Motorist - Property Damage" || cov.name === "First Party Benefits" || cov.name === "Extraordinary Medical Benefits" || cov.name === "Uninsured Motorists Bodily Injury & Underinsured Motorists Bodily Injury" || cov.name === "Uninsured Motorist Bodily Injury & Underinsured Motorist Bodily Injury" || cov.name === "Uninsured-Underinsured Motorists - Property Damage" || cov.name === "Uninsured-Underinsured Motorists Bodily Injury" || (cov.name === "Regular Use of Cars Owned by Others" && cov.selected && !_.isEmpty(cov.vehicleCovCosts_ML))),
                    content: 'Not Included'            
                },
                [`displayMessageIncluded${index}`]: {
                    visible: !(hasSuspendedLiability(vehicle) || isStorageOrCamperTrailer(vehicle)) && !loading && !!cov.selected && cov.name === "Regular Use of Cars Owned by Others" && !getVehicleCostForCoverage(cov) && !_.isEmpty(cov.vehicleCovCosts_ML),
                    content: 'Included'
                },
                [`displayOnlyCoverageSection${index}`]: {
                    visible: isDisplayOnly
                },

                [`displayOnlyVehicleCost${index}`]: {
                   visible:!disableMexicoAndDeclining && !!vehicle && hasVehicleCovCosts(cov),
                    content: getVehicleCostForCoverage(cov)
},
                [`displayOnlyLineCost${index}`]: {
                    visible: !vehicle && !!cov.amount,
                    content: formatAmountString(cov.amount)
                },
               [`validationMsgDiv${index}`]: {
                    visible: cov?.code_ML === 'PAUMBI_Ext' ? (vehicle.vehicleNumber_ML == 1 && !!cov?.validationMessage_ML) : !!cov?.validationMessage_ML,
                    content: cov?.validationMessage_ML,
                    className: cov?.code_ML === 'PAUMBI_Ext' ? (vehicle.vehicleNumber_ML == 1 ? 'ml-scrolling-table__body-option ml-popover ml-popover-small popover-error_plus' : '') : 'ml-scrolling-table__body-option ml-popover ml-popover-small popover-error'
                },
                [`covTermComponent${index}`]: {
                    visible: !hasScheduleItems(cov),
                    coverage: cov,
                    index,
                    isDisplayOnly,
                    hideMexicoDecliningAmount: disableMexicoAndDeclining
                },
                [`emptyScheduleItemsIterable${index}`]: {
                    visible: hasEmptyScheduleItems(cov, 'excludeNamedDrivers'),
                    data: cov.terms
                },
                [`scheduledItemsRow${index}`]: {
                    visible: (cov.required || cov.selected) && hasScheduleItems(cov)
                },
                [`scheduledItemsComponent${index}`]: {
                    clause: cov,
                    onCovChange,
                    onGeneralChange,
                    loading
                }
            };
        });

        return Object.assign({}, ...covOverrides);
    }, [coverageSelectedChanged, filterData, formatAmountString, getPolicyLevelCostForCoverage, getVehicleCostForCoverage, hasEmptyScheduleItems, hasVehicleCovCosts, isDisplayOnly, isVehicleLevel, loading, model, onCovChange, onGeneralChange, vehicle]);
    const overrides = {
        modelContainer: {
            visible: model.length > 0,
            className: isDisplayOnly ? 'display-only' : ''
        },
        coverageIterable: {
            data: getFilteredCovs()
        },
        ...generateCovOverrides()
    };

    const resolvers = {
        resolveComponentMap: {
            mlpaagentcovtermcomponent: MLPAAgentCovTermComponent,
            mlclausescheduleditems: MLClauseScheduledItemsComponent
        },
        resolveClassNameMap: styles
    };

    return (
        <MetadataContent
            uiProps={metadata.componentContent}
            overrideProps={overrides}
            {...resolvers} />
    );
}

MLPAEditableCoveragesComponent.propTypes = {
    model: PropTypes.arrayOf(PropTypes.shape({})),
    onCovChange: PropTypes.func,
    loading: PropTypes.bool,
    onGeneralChange: PropTypes.func,
    isDisplayOnly: PropTypes.bool,
    allowSelectCoverage: PropTypes.bool,
    level: PropTypes.string,
    vehicle: PropTypes.shape({}),
    displayedLineCoverages: PropTypes.arrayOf(PropTypes.shape({}))
};

MLPAEditableCoveragesComponent.defaultProps = {
    model: [],
};

export default MLPAEditableCoveragesComponent;
